import {
  type AstroButtonData,
  type Link,
  type TakeoverBannerData,
} from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import styled from 'styled-components'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Body, Headline } from '@shipt/design-system-typography'
import { Image as NextImage } from '@/features/shared/elements/Image/NextImage'
import { Column } from '@shipt/design-system-layouts'
import { Button } from '@shipt/design-system-buttons'
import { DynamicRetailerSelectionDrawer } from '@/features/cms/components/CMS/components/RetailerSelectionDrawer/DynamicRetailerSelectionDrawer'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { BREAKPOINTS, screenSizes } from '@shipt/design-system-themes'
import { lgTabletBreakpoint, segwayScreenSizes } from '@/features/shared/constants/screenSizes'
import { useHydrated } from '@/features/shared/components/ClientOnly'
import { theme } from '@/features/shared/constants/shiptTheme'

type CtaState =
  | { type: 'none' }
  | {
      type: 'action'
      buttonData: AstroButtonData
      actionContent: NonNullable<TakeoverBannerData['data']['action_content']>[0]
    }
  | {
      type: 'link'
      buttonData: AstroButtonData
      link: Link
    }

function getCtaState(cmsData?: TakeoverBannerData['data']): CtaState {
  if (!cmsData?.cta_button.data) {
    return { type: 'none' }
  }
  const buttonData = cmsData.cta_button.data
  if (cmsData.action_content?.[0]) {
    return {
      type: 'action',
      buttonData,
      actionContent: cmsData.action_content[0],
    }
  }
  if (buttonData.link) {
    return {
      type: 'link',
      buttonData,
      link: buttonData.link,
    }
  }
  return { type: 'none' }
}

export const TakeoverBanner = ({ content_type_id, id, data }: TakeoverBannerData) => {
  const hydrated = useHydrated()
  const { openDialog } = useHandleDialog()
  const { heading, subheading, foreground_logo, cta_button } = data
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()

  const ctaState = getCtaState(data)
  // if ctaState none, should we issue log warn? We aren't rendering a button if
  // certain conditions are not met
  const handleClick = () => {
    trackCMSElementClicked({
      shelf_index: 0,
      shelf_name: content_type_id,
      link_name: cta_button.data?.text,
      content: heading,
      type: 'button',
    })
    if (
      ctaState.type === 'action' &&
      // CMS may add more content types in the future
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ctaState.actionContent.content_type_id === 'retailer_selection_drawer'
    ) {
      openDialog(DynamicRetailerSelectionDrawer, ctaState.actionContent)
    }
  }

  const handleOnInView = () => {
    // what properties go here
    trackCMSElementViewed({
      shelf_index: 0,
      shelf_name: content_type_id,
      link_name: cta_button.data?.text,
      content: heading,
      type: 'button',
    })
  }
  try {
    return (
      <ScrollIntoView onInView={handleOnInView}>
        <HeroLayoutContainer {...getCMSWrapperProps({ id, content_type_id })}>
          {!!data.mobile_web_image && (
            <MobileImage
              className="fullWidth"
              src={data.mobile_web_image.src}
              alt={data.mobile_web_image.alt}
              fill
              quality={100}
              preload={hydrated && window.innerWidth < BREAKPOINTS.SMALL}
              sizes="100vw"
            />
          )}
          {!!data.tablet_web_image && (
            <TabletImage
              className="fullWidth"
              src={data.tablet_web_image.src}
              alt={data.tablet_web_image.alt}
              fill
              quality={90}
              preload={
                hydrated &&
                window.innerWidth >= BREAKPOINTS.SMALL &&
                window.innerWidth <= lgTabletBreakpoint
              }
              sizes="100vw"
            />
          )}
          {!!data.desktop_web_image && (
            <DesktopImage
              className="fullWidth"
              src={data.desktop_web_image.src}
              alt={data.desktop_web_image.alt}
              fill
              quality={100}
              preload={hydrated && window.innerWidth >= lgTabletBreakpoint}
              sizes="2500px"
            />
          )}
          <Gradient className="fullWidth">
            <ContentContainer align={{ md: 'start' }} spacing={{ base: 'sm', md: 'md' }}>
              <ContentColumn align={{ md: 'start' }} spacing={{ base: undefined, md: 'xs' }}>
                {!!foreground_logo && (
                  <Logo
                    height={foreground_logo.height}
                    width={foreground_logo.width}
                    preload
                    src={foreground_logo.src}
                    alt={foreground_logo.alt}
                  />
                )}
                <HeroHeadline>{heading}</HeroHeadline>
                {!!subheading?.elements && (
                  <Subheading as="div">{subheading.elements.map(renderElement)}</Subheading>
                )}
              </ContentColumn>
              {ctaState.type === 'action' && (
                <Button
                  size="md"
                  surface={ctaState.buttonData.surface}
                  concept={ctaState.buttonData.concept}
                  onClick={handleClick}
                >
                  {ctaState.buttonData.text}
                </Button>
              )}
              {ctaState.type === 'link' && (
                <ButtonLink
                  size="md"
                  surface={ctaState.buttonData.surface}
                  concept={ctaState.buttonData.concept}
                  href={ctaState.link.url}
                  opensInNewTab={ctaState.link.open_in_new_tab ?? false}
                >
                  {ctaState.buttonData.text}
                </ButtonLink>
              )}
            </ContentContainer>
          </Gradient>
        </HeroLayoutContainer>
      </ScrollIntoView>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const Gradient = styled(GridLayoutContainer)`
  width: 100%;

  @media ${segwayScreenSizes.max_lgTablet} {
    /* This component must be set to position relative to show, due the absolute positioned images above it. */
    position: relative;
    padding: 16px 0 8px;
    text-align: center;
    background: linear-gradient(
      180deg,
      rgb(255 255 255 / 0%) 0%,
      rgb(255 255 255 / 90%) 12.5%,
      ${theme.white} 100%
    );
  }
`
const Logo = styled(NextImage)`
  max-width: 155px;
  height: auto;

  @media ${screenSizes.tablet} {
    max-width: 217px;
  }
`
const HeroHeadline = styled(Headline)`
  font-size: 30px;

  @media ${screenSizes.tablet} and ${segwayScreenSizes.max_lgTablet} {
    font-size: 36px;
  }

  @media ${segwayScreenSizes.lgTablet} {
    font-size: 48px;
  }
`
const MobileImage = styled(NextImage)`
  object-fit: cover;

  @media ${screenSizes.lgMobile} {
    display: none;
  }
`
const TabletImage = styled(NextImage)`
  display: none;

  @media ${screenSizes.lgMobile} and ${segwayScreenSizes.max_lgTablet} {
    display: flex;
    object-fit: cover;
  }

  @media ${segwayScreenSizes.lgTablet} {
    font-size: 48px;
  }
`
const DesktopImage = styled(NextImage)`
  display: none;

  @media ${segwayScreenSizes.lgTablet} {
    display: flex;
    object-fit: cover;
    max-width: 2500px;
    margin: 0 auto;
  }
`
const ContentContainer = styled(Column)`
  width: 100%;
  position: relative;

  @media ${segwayScreenSizes.max_lgTablet} {
    align-items: center;
  }
`
const ContentColumn = styled(Column)`
  @media ${segwayScreenSizes.max_lgTablet} {
    align-items: center;
  }
`
const HeroLayoutContainer = styled(GridLayoutContainer)`
  place-items: end center;
  height: 23.8rem;
  position: relative;

  @media ${segwayScreenSizes.lgTablet} {
    place-items: center;
  }
`
const Subheading = styled(Body)`
  & > * {
    color: ${theme.color.content.base.neutral.secondary};
  }
`
